// store.ts
import { AnyAction, combineReducers, Reducer } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import {
	persistStore as createPersistStore,
	persistReducer as createPersistReducer,
	createMigrate,
	createTransform,
	FLUSH,
	REHYDRATE,
	PAUSE,
	PERSIST,
	PURGE,
	REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { configureStore, Action } from '@reduxjs/toolkit';
import authReducer from './stores/authStore';
import alertReducer from './stores/alertStore';
import clientReducer from './stores/clientStore';
import projectReducer from './stores/projectStore';
import companyReducer from './stores/companyStore';
import modalReducer from './stores/modalStore';
import templateReducer from './stores/templateStore';
import projectDataReducer from './stores/projectDataStore';
import projectConfigurationReducer from './stores/projectConfigurationStore';
import companyExternalUserReducer from './stores/companyExternalUser';
import userGroupReducer from './stores/userGroupStore';

const CURRENT_VERSION = 1;

const migrations = {
	// If needed, add migration functions here.
	// They should take the previous state and return the new state.
};

/**
 * We persist most of authState (user, preferences, etc.) so the app can keep
 * state across reloads, but the access token must remain memory-only.
 */
const authStateTransform = createTransform(
	(inboundState: any) => {
		// Called before state is persisted to storage
		if (!inboundState) return inboundState;
		return {
			...inboundState,
			astroToken: '',
		};
	},
	(outboundState: any) => {
		// Called when state is rehydrated from storage
		if (!outboundState) return outboundState;
		return {
			...outboundState,
			astroToken: '',
		};
	},
	{ whitelist: ['authState'] }
);

const persistConfig = {
	key: 'root',
	storage,
	whitelist: ['authState'],
	version: CURRENT_VERSION,
	transforms: [authStateTransform],
	migrate: createMigrate(migrations, {
		debug: process.env.NODE_ENV === 'development',
	}),
};

const rootReducer = combineReducers({
	authState: authReducer,
	alertState: alertReducer,
	clientState: clientReducer,
	projectState: projectReducer,
	companyState: companyReducer,
	modalState: modalReducer,
	templateState: templateReducer,
	projectData: projectDataReducer,
	projectConfigurationState: projectConfigurationReducer,
	companyExternalUserState: companyExternalUserReducer,
	userGroupState: userGroupReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

// redux-persist's types model rehydration as Partial<T>, which doesn't line up
// cleanly with combineReducers' stricter reducer typing.
// Casting here keeps app typing sane while retaining runtime behavior.
const persistedReducer = createPersistReducer(
	persistConfig as any,
	rootReducer as any
) as unknown as Reducer<RootState, AnyAction>;

const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [
					FLUSH,
					REHYDRATE,
					PAUSE,
					PERSIST,
					PURGE,
					REGISTER,
					'company/manageSubscription/fulfilled',
					'company/manageSubscription/rejected',
					'company/manageSubscription/pending',
				],
			},
		}),
});

export type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;
export type AppThunk<ReturnType = Promise<any>> = ThunkAction<
	ReturnType,
	RootState,
	unknown,
	Action<string>
>;

export default store;

export const persistStore = createPersistStore(store);
